import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, Bell, PoundSterling, MapPin, CalendarDays } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TodayAtAGlance — the "First Daily Habit" strip rendered on the Work Mode
 * entry screen. It surfaces exactly 5 signals the instructor uses to start
 * the day: today's lesson count, next pickup, time to leave, messages ready,
 * and earnings snapshot. Fails silent when there's no data.
 */
export default function TodayAtAGlance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/work-mode/today-at-a-glance`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const tile = "rounded-xl bg-slate-800/70 border border-slate-700 p-3 flex items-start gap-2";
  const leavePhrase = (m) => {
    if (m == null) return null;
    if (m <= 0) return "Leave now";
    if (m < 60) return `Leave in ${m}m`;
    const h = Math.floor(m / 60), rm = m % 60;
    return `Leave in ${h}h ${rm}m`;
  };

  return (
    <div data-testid="today-at-a-glance" className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 p-3 mb-2">
      <div className="flex items-center gap-2 mb-2">
        <CalendarDays className="w-4 h-4 text-indigo-400" />
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Today at a glance</p>
      </div>

      {data.next_lesson ? (
        <div data-testid="tag-next-lesson" className={`${tile} mb-2`}>
          <Clock className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-white truncate">
              Next: {data.next_lesson.pupil_name}{data.next_lesson.time ? ` · ${data.next_lesson.time}` : ""}
            </p>
            {data.next_lesson.pickup && (
              <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {data.next_lesson.pickup}
              </p>
            )}
            {leavePhrase(data.time_to_leave) && (
              <p className="text-[11px] font-extrabold text-emerald-400 mt-0.5">{leavePhrase(data.time_to_leave)}</p>
            )}
          </div>
        </div>
      ) : (
        data.lessons_today === 0 && (
          <div data-testid="tag-empty" className={`${tile} mb-2`}>
            <Clock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-300">No lessons scheduled today.</p>
          </div>
        )
      )}

      <div className="grid grid-cols-3 gap-2">
        <div data-testid="tag-lessons-today" className={tile}>
          <CalendarDays className="w-3.5 h-3.5 text-indigo-400 mt-0.5" />
          <div>
            <p className="text-lg leading-none font-extrabold text-white">{data.lessons_today}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Lessons</p>
          </div>
        </div>
        <div data-testid="tag-messages" className={tile}>
          <Bell className="w-3.5 h-3.5 text-amber-400 mt-0.5" />
          <div>
            <p className="text-lg leading-none font-extrabold text-white">{data.unread_messages}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Messages</p>
          </div>
        </div>
        <div data-testid="tag-earnings" className={tile}>
          <PoundSterling className="w-3.5 h-3.5 text-emerald-400 mt-0.5" />
          <div>
            <p className="text-lg leading-none font-extrabold text-white">£{data.earnings_today?.toFixed?.(0) ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Today</p>
          </div>
        </div>
      </div>
    </div>
  );
}
